import {updateURLQueryParam} from "../../other/utils/urlUtils.jsx";
import {useCallback, useEffect, useRef} from "react";
import {EDITOR_KEYS} from "../../other/constants/EditorKeys.jsx";
import {DEEP_LINK_URL_KEYS, ENTITY_TYPES} from "../../other/constants/OtherKeys.jsx";
import {showToast} from "../../other/utils/utils.jsx";

export const DeepLinkManagerSpecs = {
  generateDeepLinkInURL: (_deepLinkParams = {}) => null,
  generateDeepLink: (_deepLinkParams = {}) => null,
}

const editorDeepLinkHandler = (project, editorHandler, isBoardLoadFromUrlSuccess, entityManager) => {
  const searchParams = new URLSearchParams(window.location.search);
  const tab = searchParams.get(DEEP_LINK_URL_KEYS.tab);

  const showRecordNoExistsMsg = (type) => {
    showToast({ msg: `Failed to open share link, ${type} record doesn't exists`, type: "error", duration: "long" });
  }

  const getNoteRecordIfHas = () => {
    if (searchParams.has(DEEP_LINK_URL_KEYS.note)) {
      return project.boardNotesStore.getById(+searchParams.get(DEEP_LINK_URL_KEYS.note));
    }
    return null;
  }

  const getChecklistRecordIfHas = () => {
    if (searchParams.has(DEEP_LINK_URL_KEYS.checklist)) {
      return project.checklistStore.getById(+searchParams.get(DEEP_LINK_URL_KEYS.checklist));
    }
    return null;
  }

  // if board didn't load from url then no need to open the deep link for the following editors
  if (isBoardLoadFromUrlSuccess) {
    if (searchParams.has(DEEP_LINK_URL_KEYS.task)) {
      const taskId = +searchParams.get(DEEP_LINK_URL_KEYS.task);
      const taskRecord = project.extraTaskStore.getById(taskId);
      if (taskRecord) {
        entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: taskRecord.id, otherParams: {
          editNote: getNoteRecordIfHas()?.id,
          editChecklist: getChecklistRecordIfHas()?.id
        }});
      } else showRecordNoExistsMsg(DEEP_LINK_URL_KEYS.task);
    } else if (searchParams.has(DEEP_LINK_URL_KEYS.resource)) {
      const resourceId = +searchParams.get(DEEP_LINK_URL_KEYS.resource);
      const resourceRecord = project.resourceStore.getById(resourceId);
      if (resourceRecord) editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, resourceRecord, { resource: resourceRecord, selectTabByDeepLinkSlugOnOpen: tab, note: getNoteRecordIfHas(), checklist: getChecklistRecordIfHas() });
      else showRecordNoExistsMsg(DEEP_LINK_URL_KEYS.resource);
    } else if (searchParams.has(DEEP_LINK_URL_KEYS.client)) {
      const clientId = +searchParams.get(DEEP_LINK_URL_KEYS.client);
      const clientRecord = project.clientsStore.getById(clientId);
      if (clientRecord) editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, clientRecord, { client: clientRecord, selectTabByDeepLinkSlugOnOpen: tab, note: getNoteRecordIfHas(), checklist: getChecklistRecordIfHas() });
      else showRecordNoExistsMsg(DEEP_LINK_URL_KEYS.client);
    }
  }
  // Below editors does not require any board-specific record,
  // Therefore they can be loaded if the board was not loaded via deeplink
  if (searchParams.has(DEEP_LINK_URL_KEYS.personalSetting)) {
    const recordId = searchParams.get(DEEP_LINK_URL_KEYS.personalSetting);
    editorHandler.openEditor(EDITOR_KEYS.settingPersonalEditor, null, { selectTabByTypeOnOpen: recordId }, { by: "recordType", useExisting: true });
  } else if (searchParams.has(DEEP_LINK_URL_KEYS.generalSetting)) {
    const recordId = searchParams.get(DEEP_LINK_URL_KEYS.generalSetting);
    editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: recordId }, { by: "recordType", useExisting: true });
  }
}

const getIsOpenViaDeepLink = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return Object.values(DEEP_LINK_URL_KEYS).some((linkKey) => searchParams.has(linkKey));
}

const useDeepLinkManager = ({
  isAppLoaded, editorHandler, project, isBoardLoadFromUrlSuccess, entityManager
}) => {
  const isDeepLinkOpened = useRef(false);
  const isLoadViaDeepLink = useRef(getIsOpenViaDeepLink());

  const openDeepLinkFromURL = useCallback((isBoardLoadFromUrlSuccess) => {
    editorDeepLinkHandler(project, editorHandler, isBoardLoadFromUrlSuccess, entityManager);
  }, [project, editorHandler, entityManager]);

  const clearDeepLinkInURL = useCallback(() => {
    Object.values(DEEP_LINK_URL_KEYS).forEach((queryParamKey) => {
      updateURLQueryParam(queryParamKey, undefined, true);
    });
  }, []);

  const generateDeepLinkInURL = (deepLinkParams = {}) => {
    clearDeepLinkInURL();
    Object.entries(deepLinkParams).forEach(([queryParamKey, queryParamValue]) => {
      updateURLQueryParam(queryParamKey, queryParamValue, !queryParamValue);
    });
  }

  const generateDeepLink = (deepLinkParams = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(deepLinkParams).forEach(([queryParamKey, queryParamValue]) => {
      searchParams.set(queryParamKey, queryParamValue);
    });
    return window.location.href + '?' + encodeURI(searchParams.toString());
  }

  useEffect(() => {
    if (isAppLoaded && editorHandler && !isDeepLinkOpened.current && project) {
      openDeepLinkFromURL(isBoardLoadFromUrlSuccess);
      isDeepLinkOpened.current = true;
      setTimeout(() => {
        clearDeepLinkInURL();
      }, 3000);
    }
  }, [isAppLoaded, isBoardLoadFromUrlSuccess, editorHandler, project, openDeepLinkFromURL, clearDeepLinkInURL]);

  return {
    openDeepLinkFromURL,
    generateDeepLinkInURL,
    generateDeepLink,
    clearDeepLinkInURL,
    isLoadViaDeepLink: isLoadViaDeepLink.current,
  }
};

export default useDeepLinkManager;
